import { gql } from "~/gql"
import { useQuery } from "@oysterhr/core"
import { textValueToBoolean } from "~/app/utils/helpers"
import { useMemo } from "react"
import { mapRestrictiveCovenants } from "../helpers/serverClientMapping"
import { RestrictiveCovenantsFormValues } from "../types"

const RESTRICTIVE_COVENANTS_INTELLIGENCE_QUERY = gql(`
  query RestrictiveCovenantsIntelligence(
    $countryCode: String!
    $subdivisionCode: String
    $subdivisionCodes: [String!]
    $nonCompetition: Boolean
    $nonSolicitationEmployees: Boolean
    $nonSolicitationCustomers: Boolean
  ) {
    countryIntelligence(countryCode: $countryCode, subdivisionCodes: $subdivisionCodes) {
      companyAreaOfBusinessFieldForSubdivisionCodes
      companyAreaOfBusinessFieldRequired
      restrictiveCovenants {
        ...RestrictiveCovenantsRules
      }
    }
  }
`)

export type KBParams = Partial<
  Pick<
    RestrictiveCovenantsFormValues,
    "nonCompetition" | "nonSolicitation" | "nonSolicitationOption"
  >
>

type Options = {
  countryCode: string
  subdivisionCode?: string | null
} & KBParams

export function useRestrictiveCovenantsIntelligence({
  countryCode,
  subdivisionCode,
  nonCompetition,
  nonSolicitationOption,
}: Options) {
  const params = useMemo(() => {
    const nonSolicitationOptionsServer =
      mapRestrictiveCovenants.clientToServer.nonSolicitationOptions(nonSolicitationOption)
    const nonCompetitionParam = textValueToBoolean(nonCompetition)
    return {
      nonCompetition: nonCompetitionParam ?? undefined,
      nonSolicitationEmployees: nonSolicitationOptionsServer?.nonSolicitationEmployees ?? undefined,
      nonSolicitationCustomers: nonSolicitationOptionsServer?.nonSolicitationCustomers ?? undefined,
    }
  }, [nonCompetition, nonSolicitationOption])

  return useQuery(RESTRICTIVE_COVENANTS_INTELLIGENCE_QUERY, {
    variables: {
      countryCode,
      subdivisionCodes: subdivisionCode ? [subdivisionCode] : null,
      // Used in RestrictiveCovenantsRules fragment
      subdivisionCode: subdivisionCode ?? undefined,
      ...params,
    },
  })
}
